import { useState } from "react";
import { useEffect } from "react";
import { Fragment } from "react";
import { connect } from "react-redux";

import { Check } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Info";
import { Grid, Zoom } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { darken, lighten, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import constants from "../../assets/constants";
import * as AssessmentSession from "../../state/redux/assessmentSession";
import * as PatientSession from "../../state/redux/patientSession";
import { formatIsoDateString } from "../../utils/helpers";
import AlertDialog from "../dialogs/alertDialog";
import AssessmentWizardDialog from "../dialogs/assessmentWizardDialog";
import OmniAvatar from "../ui/omniAvatar";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget";
import WidgetContent from "../ui/widgetContent";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const PatientProxyAssessments = props => {
  const { assessmentODMs, title = "Assessments", assessmentTemplates } = props;

  const [selectedAssessmentODM, setSelectedAssessmentODM] = useState(null);
  const [showAssessmentWizardDialog, setShowAssessmentWizardDialog] = useState(false);

  const [assessmentToDelete, setAssessmentToDelete] = useState(null);

  const getAssessmentStatus = assessment => {
    if (assessment.omniData.completeDate != null) {
      return `Completed: ${formatIsoDateString(assessment.omniData.completeDate)}`;
    }
    if (assessment.omniData.startDate != null) {
      return `Started: ${formatIsoDateString(assessment.omniData.startDate)}`;
    }
    return "Not Started";
  };

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (assessmentODMs != null) {
      setTitleText(assessmentODMs ? `${assessmentODMs.length} Completed` : "");
    } else setTitleText("");
  }, [assessmentODMs]);

  useEffect(() => {
    if (assessmentTemplates == null) {
      props.loadAssessmentTemplatesRequest();
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentTemplates]);

  const getWidgetContent = () => {
    if (assessmentODMs.length !== 0) {
      return (
        <WidgetList>
          {assessmentODMs.map((assessment, index) => {
            return (
              <ListItem
                dense
                key={`assessment${assessment._id}`}
                disableGutters={true}
                sx={{
                  padding: theme.spacing(1),
                  paddingTop: 0,
                  paddingBottom: 0,
                  marginBottom: theme.spacing(1),
                  borderRadius: theme.spacing(1),
                  boxShadow: theme.shadows[3],
                  backgroundColor: theme.palette.foreground.main,
                  color: theme.palette.foreground.contrastText,
                  cursor: "pointer",
                  "& .MuiIconButton-root": {
                    transition: "all 0.15s ease-out",
                    opacity: 0,
                  },
                  "&:hover": {
                    color: theme.palette.secondary.main,
                    background: lighten(theme.palette.secondary.main, 0.75),
                    border: `none`,
                    "& .MuiIconButton-root": {
                      opacity: 1,
                    },
                  },
                }}
              >
                <Tooltip
                  title="Assessment Total Score"
                  placement="top"
                  enterDelay={500}
                  enterNextDelay={500}
                  TransitionComponent={Zoom}
                  TransitionProps={{ timeout: 200, easing: "ease-in-out" }}
                >
                  {Object.prototype.toString.call(assessment.omniData.assessmentResults) ===
                  "[object Object]" ? (
                    <OmniAvatar
                      sx={[
                        {
                          width: 42,
                          height: 42,
                          fontSize: "1.8rem",
                          boxShadow: theme.shadows[1],
                          cursor: "unset",
                        },
                        assessment.omniData.assessmentResults.finalResult.toString().length >=
                          3 && {
                          fontSize: "1.4rem",
                        },
                        assessment.omniData.assessmentResults.finalResult.toString().length >=
                          4 && { fontSize: "1.2rem" },
                        assessment.omniData.assessmentResults.finalResult >= 14 && {
                          color: darken(theme.palette.error.main, 0.5),
                          backgroundColor: lighten(theme.palette.error.main, 0.5),
                          border: `2px solid ${darken(theme.palette.error.main, 0.5)}`,
                        },
                        assessment.omniData.assessmentResults.finalResult > 6 &&
                          assessment.omniData.assessmentResults.finalResult < 14 && {
                            color: darken(theme.palette.warning.main, 0.5),
                            backgroundColor: lighten(theme.palette.warning.main, 0.5),
                            border: `2px solid ${darken(theme.palette.warning.main, 0.5)}`,
                          },
                        assessment.omniData.assessmentResults.finalResult <= 6 && {
                          color: darken(theme.palette.success.main, 0.5),
                          backgroundColor: lighten(theme.palette.success.main, 0.5),
                          border: `2px solid ${darken(theme.palette.success.main, 0.5)}`,
                        },
                        (assessment.omniData.omniType === constants.ar1001Prescreen ||
                          assessment.omniData.omniType ===
                            constants.assessmentIDs.omniHealthHistory) && {
                          color: darken(theme.palette.success.main, 0.5),
                          backgroundColor: lighten(theme.palette.success.main, 0.5),
                          border: `2px solid ${darken(theme.palette.success.main, 0.5)}`,
                        },
                      ]}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      {assessment.omniData.omniType === constants.ar1001Prescreen ||
                      assessment.omniData.omniType === constants.assessmentIDs.omniHealthHistory ? (
                        <Check />
                      ) : (
                        assessment.omniData.assessmentResults.finalResult
                      )}
                    </OmniAvatar>
                  ) : (
                    <OmniAvatar
                      sx={[
                        {
                          width: 42,
                          height: 42,
                          fontSize: "1.8rem",
                          boxShadow: theme.shadows[1],
                          cursor: "unset",
                        },
                        (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                          assessment.omniData.omniType !==
                            constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults
                            .find(scoringMethod => scoringMethod.isDefault === true)
                            .finalResult.toString().length >= 3 && {
                            fontSize: "1.4rem",
                          },
                        (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                          assessment.omniData.omniType !==
                            constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults
                            .find(scoringMethod => scoringMethod.isDefault === true)
                            .finalResult.toString().length >= 4 && { fontSize: "1.2rem" },
                        (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                          assessment.omniData.omniType !==
                            constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults.find(
                            scoringMethod => scoringMethod.isDefault === true,
                          ).finalResult >= 14 && {
                            color: darken(theme.palette.error.main, 0.5),
                            backgroundColor: lighten(theme.palette.error.main, 0.5),
                            border: `2px solid ${darken(theme.palette.error.main, 0.5)}`,
                          },
                        (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                          assessment.omniData.omniType !==
                            constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults.find(
                            scoringMethod => scoringMethod.isDefault === true,
                          ).finalResult > 6 &&
                          (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                            assessment.omniData.omniType !==
                              constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults.find(
                            scoringMethod => scoringMethod.isDefault === true,
                          ).finalResult < 14 && {
                            color: darken(theme.palette.warning.main, 0.5),
                            backgroundColor: lighten(theme.palette.warning.main, 0.5),
                            border: `2px solid ${darken(theme.palette.warning.main, 0.5)}`,
                          },
                        (assessment.omniData.omniType !== constants.ar1001Prescreen ||
                          assessment.omniData.omniType !==
                            constants.assessmentIDs.omniHealthHistory) &&
                          assessment.omniData.assessmentResults.find(
                            scoringMethod => scoringMethod.isDefault === true,
                          ).finalResult <= 6 && {
                            color: darken(theme.palette.success.main, 0.5),
                            backgroundColor: lighten(theme.palette.success.main, 0.5),
                            border: `2px solid ${darken(theme.palette.success.main, 0.5)}`,
                          },
                      ]}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      {
                        assessment.omniData.assessmentResults.find(
                          scoringMethod => scoringMethod.isDefault === true,
                        ).finalResult
                      }
                    </OmniAvatar>
                  )}
                </Tooltip>
                <ListItemText
                  primary={
                    <Typography
                      component="span"
                      variant="h6"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginLeft: theme.spacing(1),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginTop: 0,
                        color: theme.palette.commonground.contrastText,
                        webkitUserSelect: "none",
                        userSelect: "none",
                      }}
                    >
                      {assessment.omniData.title}
                    </Typography>
                  }
                  secondary={
                    <Fragment>
                      <Typography
                        component="span"
                        variant="subtitle2"
                        color="textPrimary"
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          marginLeft: theme.spacing(1),
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          marginTop: "-6px",
                          color: theme.palette.commonground.contrastText,
                          webkitUserSelect: "none",
                          userSelect: "none",
                        }}
                      >
                        {getAssessmentStatus(assessment)}
                      </Typography>
                    </Fragment>
                  }
                  onClick={e => {
                    try {
                      e.stopPropagation();
                      setSelectedAssessmentODM(assessment);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                />

                <Tooltip title={"Click for Details"} placement="left">
                  <IconButton
                    sx={[
                      {
                        position: "absolute",
                        top: theme.spacing(1),
                        right: theme.spacing(1),
                        height: 42,
                        width: 42,
                        borderRadius: "50%",
                        "&:hover": {
                          // color: theme.palette.info.contrastText,
                          // background: getToDoMainColor(toDoODM),
                        },
                      },
                    ]}
                    size="large"
                    onClick={e => {
                      e.stopPropagation();
                      // onClickHandler(symptom);
                    }}
                  >
                    <EditIcon sx={{ height: 32, width: 32 }} />
                  </IconButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </WidgetList>
      );
    }

    return (
      <WidgetContent>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignContent="space-evenly"
          sx={{ height: "100%", color: theme.palette.commonground.contrastText }}
        >
          <Grid
            container
            item
            xs={12}
            justifyContent="center"
            sx={{ height: "56px", marginBottom: theme.spacing(1) }}
          >
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Typography variant="overline" sx={{ lineHeight: 2, height: "56px" }}>
                Looks like you haven't completed any prescreening assessments yet...
              </Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12} justifyContent="center" sx={{ height: "56px" }}>
            <Grid item>
              <OmniResponsiveButton
                buttonText="Start Prescreening"
                tooltipPlacement="bottom"
                icon={null}
                tooltipTitle="Click here to find out if you qualify for the latest trial"
                onClick={() => {
                  setShowAssessmentWizardDialog(true);
                }}
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} justifyContent="center" sx={{ height: "56px" }}>
            <Grid item>
              <Typography variant="overline" align="center">
                Click the Start button to get going!
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </WidgetContent>
    );
  };

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle title={title} text={titleText} loading={assessmentODMs == null}>
        <OmniResponsiveButton
          tooltipTitle="New Assessment"
          buttonText="Assessment"
          onClick={() => {
            setShowAssessmentWizardDialog(true);
          }}
        />
      </WidgetTitle>

      {assessmentODMs == null && <WidgetSkeleton />}
      {assessmentODMs != null && getWidgetContent()}

      {(showAssessmentWizardDialog || selectedAssessmentODM) && (
        <AssessmentWizardDialog
          open={showAssessmentWizardDialog || selectedAssessmentODM}
          displayResultsAssessmentODM={selectedAssessmentODM}
          onClose={() => {
            setSelectedAssessmentODM(null);
            setShowAssessmentWizardDialog(false);
          }}
          onAccept={() => {
            setSelectedAssessmentODM(null);
            setShowAssessmentWizardDialog(false);
          }}
        />
      )}

      {assessmentToDelete && (
        <AlertDialog
          open={assessmentToDelete}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this assessment?"}
          onAccept={() => {
            props.deleteAssessmentRequest(assessmentToDelete._id);
            setAssessmentToDelete(null);
          }}
          onClose={() => {
            setAssessmentToDelete(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  assessmentTemplates: state.assessmentSession.assessmentTemplates,
  assessmentODMs: state.userSession.userAssessments,
  patientODM: state.userSession.proxyPatient,
  userODM: state.userSession.userODM,
  awsGroup: state.userSession.awsGroup,
});

const mapDispatchToProps = {
  updateAssessmentRequest: PatientSession.updateAssessmentRequest,
  deleteAssessmentRequest: AssessmentSession.deleteAssessmentRequest,
  loadAssessmentTemplatesRequest: AssessmentSession.loadAssessmentTemplatesRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientProxyAssessments);
